import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar, Clock, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';

const AppointmentSection = () => {
  const [selectedType, setSelectedType] = useState('');
  const [showCalendly, setShowCalendly] = useState(false);

  const clientTypes = [
    {
      id: 'particulier',
      title: 'Particulier',
      description: 'Propriétaire d\'étang, bassin ou piscine naturelle',
      icon: Users,
      color: 'from-teal-500 to-cyan-500'
    },
    {
      id: 'collectivite',
      title: 'Collectivité',
      description: 'Commune, département, région ou organisme public',
      icon: Calendar,
      color: 'from-blue-500 to-indigo-500'
    },
    {
      id: 'entreprise',
      title: 'Entreprise',
      description: 'Industrie, port, marina ou site commercial',
      icon: Clock,
      color: 'from-amber-500 to-orange-500'
    }
  ];

  const handleTypeSelection = (type) => {
    setSelectedType(type);
  };

  const handleBookAppointment = () => {
    // In real implementation, this would open Calendly with the selected type
    setShowCalendly(true);
    // Mock Calendly integration
    console.log('Opening Calendly for client type:', selectedType);
  };

  return (
    <section id="rendez-vous" className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-teal-100 to-cyan-100 rounded-full blur-3xl opacity-30 translate-x-48 -translate-y-48"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full blur-3xl opacity-30 -translate-x-48 translate-y-48"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom-6 duration-800">
          <div className="inline-flex items-center px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 font-medium mb-4">
            <Calendar className="w-4 h-4 mr-2" />
            Prise de Rendez-vous
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Planifions Votre Projet
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Notre IA vous accompagne pour qualifier votre besoin et planifier un rendez-vous avec nos experts
          </p>
        </div>

        {!showCalendly ? (
          <>
            {/* Client Type Selection */}
            <div className="max-w-4xl mx-auto mb-12">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                Quel type de client êtes-vous ?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {clientTypes.map((type, index) => {
                  const IconComponent = type.icon;
                  const isSelected = selectedType === type.id;
                  return (
                    <Card 
                      key={type.id}
                      className={`cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-2 border-2 animate-in fade-in slide-in-from-bottom-6 duration-800 ${
                        isSelected 
                          ? 'border-teal-500 bg-teal-50 shadow-lg' 
                          : 'border-gray-200 hover:border-teal-300'
                      }`}
                      style={{ animationDelay: `${index * 150}ms` }}
                      onClick={() => handleTypeSelection(type.id)}
                    >
                      <CardContent className="p-6 text-center">
                        <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${type.color} rounded-2xl flex items-center justify-center transition-transform duration-300 ${
                          isSelected ? 'scale-110' : 'hover:scale-105'
                        } shadow-lg`}>
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                          {type.title}
                        </h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {type.description}
                        </p>
                        {isSelected && (
                          <div className="mt-4">
                            <CheckCircle className="w-6 h-6 text-teal-500 mx-auto animate-in zoom-in duration-300" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Qualification Questions */}
            {selectedType && (
              <div className="max-w-2xl mx-auto mb-12 animate-in fade-in slide-in-from-bottom-6 duration-500">
                <Card className="bg-white/80 backdrop-blur-sm border border-gray-200 shadow-xl">
                  <CardHeader>
                    <CardTitle className="text-center text-2xl text-gray-900">
                      Qualifions votre besoin
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="p-4 bg-teal-50 rounded-xl border border-teal-100">
                        <div className="font-medium text-gray-900 mb-2">💬 Assistant IA Good-Bloom</div>
                        <p className="text-gray-700">
                          {selectedType === 'particulier' && "Parfait ! Pour un particulier, nous proposons des solutions adaptées aux étangs privés et bassins naturels. Quelle est la superficie approximative de votre plan d'eau ?"}
                          {selectedType === 'collectivite' && "Excellent ! Pour les collectivités, nous développons des projets sur-mesure. S'agit-il d'un plan d'eau urbain, d'un canal ou d'un port ?"}
                          {selectedType === 'entreprise' && "Très bien ! Pour les entreprises, nos solutions s'adaptent aux contraintes industrielles. Quel type d'installation souhaitez-vous équiper ?"}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {selectedType === 'particulier' && (
                          <>
                            <Button variant="outline" className="text-left justify-start hover:bg-teal-50 hover:border-teal-300">
                              {"< 100 m²"}
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-teal-50 hover:border-teal-300">
                              100 - 1000 m²
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-teal-50 hover:border-teal-300">
                              1000 - 5000 m²
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-teal-50 hover:border-teal-300">
                              {"> 5000 m²"}
                            </Button>
                          </>
                        )}
                        {selectedType === 'collectivite' && (
                          <>
                            <Button variant="outline" className="text-left justify-start hover:bg-blue-50 hover:border-blue-300">
                              Bassin urbain
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-blue-50 hover:border-blue-300">
                              Canal / Rivière
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-blue-50 hover:border-blue-300">
                              Port de plaisance
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-blue-50 hover:border-blue-300">
                              Lac / Étang public
                            </Button>
                          </>
                        )}
                        {selectedType === 'entreprise' && (
                          <>
                            <Button variant="outline" className="text-left justify-start hover:bg-amber-50 hover:border-amber-300">
                              Port commercial
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-amber-50 hover:border-amber-300">
                              Site industriel
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-amber-50 hover:border-amber-300">
                              Marina / Club nautique
                            </Button>
                            <Button variant="outline" className="text-left justify-start hover:bg-amber-50 hover:border-amber-300">
                              Bassin de rétention
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Book Appointment Button */}
            {selectedType && (
              <div className="text-center animate-in fade-in slide-in-from-bottom-6 duration-500 delay-200">
                <Button 
                  onClick={handleBookAppointment}
                  size="lg"
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg group text-lg font-semibold"
                >
                  Prendre rendez-vous
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </div>
            )}
          </>
        ) : (
          /* Calendly Integration Placeholder */
          <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-6 duration-500">
            <Card className="bg-white shadow-2xl border-0">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Choisissez votre créneau
                  </h3>
                  <p className="text-gray-600">
                    Sélectionnez la date et l'heure qui vous conviennent pour un entretien avec nos experts
                  </p>
                </div>
                
                {/* Mock Calendly Interface */}
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <Calendar className="w-16 h-16 text-teal-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    Réservez un créneau
                  </h4>
                  <p className="text-gray-600 mb-6">
                    Choisissez l'horaire !
                  </p>
                  <div className="space-y-3">
                    <div className="text-sm text-gray-500">URL Calendly configurée :</div>
                    <div className="bg-white p-3 rounded-lg border font-mono text-sm text-teal-600">
                      {mockData.appointments.calendlyUrl}
                    </div>
                    <div className="text-sm text-gray-500">Type de client sélectionné : <strong>{selectedType}</strong></div>
                  </div>
                  
                  {/* Bouton principal pour ouvrir Calendly */}
                  <Button 
                    onClick={() => window.open(mockData.appointments.calendlyUrl, '_blank')}
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg mb-4"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Prendre rendez-vous sur Calendly
                  </Button>
                  
                  {/* Bouton secondaire pour fermer */}
                  <Button 
                    onClick={() => setShowCalendly(false)}
                    variant="outline"
                    className="w-full"
                  >
                    Retour
                  </Button>
                
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default AppointmentSection;